import { FLOW, PLAYER, SCORING, THREATS, WORLD } from './config';
import {
  collidesWithAnyObstacle,
  moveVehicleWithCollisions,
  movingSphereFraction,
  segmentCircleFraction,
  sweepObstacles,
} from './collision';
import { difficultyForScore, selectThreat } from './director';
import { updateDrive } from './drive';
import { createRadarState, updateRadar } from './radar';
import type {
  NormalizedInput,
  ParticleState,
  ProjectileState,
  SimulationState,
  ThreatKind,
  ThreatState,
  Vec3,
  VehicleState,
} from './models';
import { buildWorldAround, buildWorldAroundPoints } from './world';

const NO_INPUT: NormalizedInput = { leftTread: 0, rightTread: 0, firePressed: false };
const TAU = Math.PI * 2;

export function createSimulation(seed: number): SimulationState {
  return {
    mode: 'attract',
    resumeMode: null,
    resumeModeTime: 0,
    seed,
    randomState: seed >>> 0,
    elapsed: 0,
    modeTime: 0,
    score: 0,
    tanks: 3,
    awardedBonuses: 0,
    player: createPlayer(),
    playerPreviousPosition: { x: 0, y: 0, z: 0 },
    spawnProtection: 0,
    obstacles: buildWorldAround(seed, 0, 0, WORLD.activeCellRadius),
    primaryThreat: null,
    skimmer: null,
    threatIntermission: 0,
    skimmerCooldown: 22,
    radar: createRadarState(),
    feedback: {
      muzzleFlash: 0,
      recoil: 0,
      bump: 0,
      damageFlash: 0,
      bonusFlash: 0,
      message: '',
      messageTime: 0,
    },
    projectiles: [],
    particles: [],
    events: [],
    nextEntityId: 1,
  };
}

function createPlayer(): VehicleState {
  return {
    position: { x: 0, y: 0, z: 0 },
    yaw: 0,
    radius: PLAYER.radius,
    drive: { leftTread: 0, rightTread: 0 },
  };
}

export function startSimulation(state: SimulationState): void {
  if (state.mode !== 'attract' && state.mode !== 'game-over') return;
  const fresh = createSimulation(state.seed);
  Object.assign(state, fresh);
  changeMode(state, 'ready');
}

export function setSimulationPaused(state: SimulationState, paused: boolean): void {
  if (paused && (state.mode === 'ready' || state.mode === 'playing' || state.mode === 'life-lost')) {
    state.resumeMode = state.mode;
    state.resumeModeTime = state.modeTime;
    changeMode(state, 'paused');
  } else if (!paused && state.mode === 'paused' && state.resumeMode) {
    const resumeMode = state.resumeMode;
    const resumeModeTime = state.resumeModeTime;
    state.resumeMode = null;
    state.resumeModeTime = 0;
    changeMode(state, resumeMode);
    state.modeTime = resumeModeTime;
  }
}

export function updateSimulation(state: SimulationState, input: NormalizedInput, delta: number): void {
  state.events = [];
  if (state.mode === 'paused') return;
  const safeDelta = Number.isFinite(delta) ? Math.max(0, Math.min(delta, 0.1)) : 0;
  state.elapsed += safeDelta;
  state.modeTime += safeDelta;
  updateFeedback(state, safeDelta);
  updateParticles(state, safeDelta);

  if (state.mode === 'attract' || state.mode === 'game-over') return;
  if (state.mode === 'ready') {
    if (state.modeTime >= FLOW.readyDuration) enterPlaying(state);
    return;
  }
  if (state.mode === 'life-lost') {
    if (state.modeTime >= FLOW.lifeLostDuration) finishLifeLost(state);
    return;
  }

  updatePlayer(state, input, safeDelta);
  if (state.mode !== 'playing') return;
  updateDirector(state, safeDelta);
  updateThreat(state, safeDelta);
  if (state.mode !== 'playing') return;
  updateSkimmer(state, safeDelta);
  updateProjectiles(state, safeDelta);
  if (state.mode !== 'playing') return;
  if (state.spawnProtection > 0) state.spawnProtection = Math.max(0, state.spawnProtection - safeDelta);
  updateRadar(state, safeDelta);
}

export function idleSimulation(state: SimulationState, delta: number): void {
  updateSimulation(state, NO_INPUT, delta);
}

function enterPlaying(state: SimulationState): void {
  changeMode(state, 'playing');
  state.spawnProtection = FLOW.spawnProtection;
  state.threatIntermission = randomRange(state, FLOW.threatIntermissionMin, FLOW.threatIntermissionMax);
}

function finishLifeLost(state: SimulationState): void {
  if (state.tanks <= 0) {
    changeMode(state, 'game-over');
    return;
  }
  const deathPosition = { ...state.player.position };
  const player = createPlayer();
  player.position = findSafeRespawn(state, deathPosition);
  state.player = player;
  refreshActiveWorld(state);
  changeMode(state, 'ready');
}

function updatePlayer(state: SimulationState, input: NormalizedInput, delta: number): void {
  state.playerPreviousPosition = { ...state.player.position };
  const motion = updateDrive(state.player.drive, input, delta);
  state.player.drive = motion.drive;
  state.player.yaw = normalizeAngle(state.player.yaw + motion.angularSpeed * delta);
  const movement = {
    x: -Math.sin(state.player.yaw) * motion.linearSpeed * delta,
    z: Math.cos(state.player.yaw) * motion.linearSpeed * delta,
  };
  const moved = moveVehicleWithCollisions(state.player, movement, state.obstacles);
  const tankThreat = state.primaryThreat?.kind === 'missile' ||
    (state.primaryThreat && !['enter', 'aim'].includes(state.primaryThreat.aiMode))
    ? null
    : state.primaryThreat;
  let tankFraction = tankThreat
    ? segmentCircleFraction(
        state.player.position,
        moved.position,
        tankThreat.position,
        state.player.radius + tankThreat.radius,
      )
    : null;
  if (tankThreat && tankFraction !== null) {
    const earlyEvent = resolvePlayerShellEventBeforeBody(
      state,
      tankThreat,
      tankThreat.position,
      delta,
      tankFraction,
      state.playerPreviousPosition,
      moved.position,
    );
    if (state.mode !== 'playing') return;
    if (earlyEvent === 'threat') tankFraction = null;
  }
  if (tankFraction !== null) {
    if (state.spawnProtection <= 0) {
      destroyPlayer(state);
      return;
    }
    const travel = Math.hypot(moved.position.x - state.player.position.x, moved.position.z - state.player.position.z);
    const safeFraction = travel > 0 ? Math.max(0, tankFraction - 0.02 / travel) : 0;
    state.player.position = interpolatePoint(state.player.position, moved.position, safeFraction);
  } else state.player.position = moved.position;
  if (moved.bumped || tankFraction !== null) {
    state.events.push({ type: 'bump' });
    state.feedback.bump = 0.14;
  }
  refreshActiveWorld(state);
  if (
    input.firePressed &&
    state.spawnProtection <= 0 &&
    !state.projectiles.some((projectile) => projectile.owner === 'player')
  ) {
    firePlayerShell(state);
  }
}

function updateDirector(state: SimulationState, delta: number): void {
  if (!state.primaryThreat) {
    if (!state.projectiles.some((projectile) => projectile.owner === 'hostile')) {
      state.threatIntermission -= delta;
      if (state.threatIntermission <= 0) spawnPrimaryThreat(state);
    }
  }
  if (!state.skimmer) {
    state.skimmerCooldown -= delta;
    if (state.skimmerCooldown <= 0) spawnSkimmer(state);
  }
}

export function spawnPrimaryThreat(state: SimulationState, forcedKind?: ThreatKind): ThreatState | null {
  if (state.primaryThreat) return state.primaryThreat;
  const kind = forcedKind ?? selectThreat(state.score, nextRandom(state));
  const radius = kind === 'missile' ? 1.5 : kind === 'siege' ? 2.2 : 2;
  for (let attempt = 0; attempt < 24; attempt += 1) {
    const offscreenBias = Math.PI + randomRange(state, -1.2, 1.2);
    const bearing = state.player.yaw + offscreenBias;
    const distance = randomRange(state, THREATS.spawnMin, THREATS.spawnMax);
    const position = {
      x: state.player.position.x - Math.sin(bearing) * distance,
      y: kind === 'missile' ? 5.2 : 0,
      z: state.player.position.z + Math.cos(bearing) * distance,
    };
    if (collidesWithAnyObstacle(position, radius + 1, state.obstacles)) continue;
    if (!hasLineOfSight(position, state.player.position, state)) continue;
    const threat: ThreatState = {
      id: state.nextEntityId++,
      kind,
      position,
      previousPosition: { ...position },
      yaw: normalizeAngle(bearing + Math.PI),
      radius,
      aiMode: 'enter',
      aiTime: 0,
      cooldown: THREATS.enterWarning,
      weavePhase: randomRange(state, 0, TAU),
      flankDirection: nextRandom(state) < 0.5 ? -1 : 1,
    };
    state.primaryThreat = threat;
    return threat;
  }
  state.threatIntermission = 0.25;
  return null;
}

function updateThreat(state: SimulationState, delta: number): void {
  const threat = state.primaryThreat;
  if (!threat) return;
  threat.previousPosition = { ...threat.position };
  threat.aiTime += delta;
  if (threat.kind === 'missile') {
    updateMissile(state, threat, delta);
    return;
  }
  updateTankThreat(state, threat, delta);
}

function updateTankThreat(state: SimulationState, threat: ThreatState, delta: number): void {
  const difficulty = difficultyForScore(state.score);
  threat.cooldown = Math.max(0, threat.cooldown - delta);
  if (threat.aiMode === 'enter' || threat.aiMode === 'aim') {
    const startingBodyFraction = movingGroundFraction(
      threat.position,
      threat.position,
      state.playerPreviousPosition,
      state.player.position,
      threat.radius + state.player.radius,
    );
    if (
      startingBodyFraction !== null &&
      resolvePlayerShellEventBeforeBody(state, threat, threat.position, delta, startingBodyFraction) === 'threat'
    ) return;
    if (state.mode !== 'playing') return;
    if (resolveTankPlayerContact(state, threat, threat.position)) return;
  }
  const dx = state.player.position.x - threat.position.x;
  const dz = state.player.position.z - threat.position.z;
  const distance = Math.hypot(dx, dz);
  const targetYaw = Math.atan2(-dx, dz);
  const turnRate = threat.kind === 'siege' ? 1.55 : 1.05;

  if (threat.aiMode === 'enter') {
    threat.yaw = turnToward(threat.yaw, targetYaw, turnRate * delta);
    if (threat.aiTime >= THREATS.enterWarning) {
      threat.aiMode = 'seek';
      threat.aiTime = 0;
    }
    return;
  }

  if (threat.aiMode === 'aim') {
    const telegraphedYaw = normalizeAngle(targetYaw + (threat.aimOffset ?? 0));
    threat.yaw = turnToward(threat.yaw, telegraphedYaw, turnRate * 0.72 * delta);
    const aimTime = (threat.kind === 'siege' ? THREATS.siegeAimTime : THREATS.scoutAimTime) * difficulty.timingMultiplier;
    if (
      threat.aiTime >= aimTime &&
      !state.projectiles.some((projectile) => projectile.owner === 'hostile')
    ) {
      fireHostileShell(state, threat);
      const cooldownRange = threat.kind === 'siege' ? THREATS.siegeCooldown : THREATS.scoutCooldown;
      threat.cooldown = randomRange(state, cooldownRange[0], cooldownRange[1]) * difficulty.timingMultiplier;
      threat.aiMode = 'evade';
      threat.aiTime = 0;
    }
    return;
  }

  let steeringYaw = targetYaw;
  if (distance < 20) steeringYaw = normalizeAngle(targetYaw + Math.PI + threat.flankDirection * 0.35);
  else if (distance < 44) steeringYaw = normalizeAngle(targetYaw + threat.flankDirection * 0.72);
  if (threat.aiMode === 'evade') steeringYaw = normalizeAngle(targetYaw + threat.flankDirection * 1.25);

  const sightClear = hasLineOfSight(threat.position, state.player.position, state);
  if (threat.kind === 'siege' && !sightClear) {
    const offsetDistance = 10;
    const leftCandidate = {
      x: threat.position.x - Math.cos(targetYaw) * offsetDistance,
      y: 0,
      z: threat.position.z - Math.sin(targetYaw) * offsetDistance,
    };
    const rightCandidate = {
      x: threat.position.x + Math.cos(targetYaw) * offsetDistance,
      y: 0,
      z: threat.position.z + Math.sin(targetYaw) * offsetDistance,
    };
    const leftClear = hasLineOfSight(leftCandidate, state.player.position, state);
    const rightClear = hasLineOfSight(rightCandidate, state.player.position, state);
    if (leftClear !== rightClear) threat.flankDirection = leftClear ? 1 : -1;
    steeringYaw = normalizeAngle(targetYaw + threat.flankDirection * 1.05);
  }

  const feelerLength = threat.kind === 'siege' ? 12 : 8;
  if (feelerBlocked(state, threat.position, threat.yaw, feelerLength, threat.radius * 0.65)) {
    const leftBlocked = feelerBlocked(state, threat.position, threat.yaw + 0.48, feelerLength, threat.radius * 0.55);
    const rightBlocked = feelerBlocked(state, threat.position, threat.yaw - 0.48, feelerLength, threat.radius * 0.55);
    const avoidance = leftBlocked === rightBlocked ? threat.flankDirection : leftBlocked ? -1 : 1;
    steeringYaw = normalizeAngle(threat.yaw + avoidance * 0.95);
  }
  threat.yaw = turnToward(threat.yaw, steeringYaw, turnRate * delta);
  const baseSpeed = threat.kind === 'siege' ? THREATS.siegeSpeed : THREATS.scoutSpeed;
  const speed = baseSpeed * difficulty.speedMultiplier * (threat.aiMode === 'evade' ? 1.05 : 1);
  const vehicle: VehicleState = {
    position: threat.position,
    yaw: threat.yaw,
    radius: threat.radius,
    drive: { leftTread: 0, rightTread: 0 },
  };
  const movement = { x: -Math.sin(threat.yaw) * speed * delta, z: Math.cos(threat.yaw) * speed * delta };
  const moved = moveVehicleWithCollisions(vehicle, movement, state.obstacles);
  const bodyFraction = movingGroundFraction(
    threat.position,
    moved.position,
    state.playerPreviousPosition,
    state.player.position,
    threat.radius + state.player.radius,
  );
  if (
    bodyFraction !== null &&
    resolvePlayerShellEventBeforeBody(state, threat, moved.position, delta, bodyFraction) === 'threat'
  ) return;
  if (state.mode !== 'playing') return;
  if (resolveTankPlayerContact(state, threat, moved.position)) return;
  threat.position = moved.position;
  if (moved.bumped) {
    threat.yaw = normalizeAngle(threat.yaw + threat.flankDirection * 0.9);
    threat.flankDirection = threat.flankDirection === 1 ? -1 : 1;
  }
  if (threat.aiMode === 'evade' && threat.aiTime > 1.1) {
    threat.aiMode = 'seek';
    threat.aiTime = 0;
  }
  if (
    threat.aiMode === 'seek' &&
    threat.cooldown <= 0 &&
    !state.projectiles.some((projectile) => projectile.owner === 'hostile') &&
    distance < 100 &&
    hasLineOfSight(threat.position, state.player.position, state)
  ) {
    const error = threat.kind === 'siege' ? difficulty.siegeErrorRadians : difficulty.scoutErrorRadians;
    threat.aimOffset = randomRange(state, -error, error);
    threat.aiMode = 'aim';
    threat.aiTime = 0;
  }
}

type EarlyShellEvent = 'none' | 'threat' | 'other';

function resolvePlayerShellEventBeforeBody(
  state: SimulationState,
  threat: ThreatState,
  nextThreatPosition: Vec3,
  delta: number,
  bodyFraction: number,
  playerStartPosition = state.playerPreviousPosition,
  playerEndPosition = state.player.position,
): EarlyShellEvent {
  const playerShell = state.projectiles.find((projectile) => projectile.owner === 'player');
  if (!playerShell) return 'none';
  const shellNext = {
    x: playerShell.position.x + playerShell.velocity.x * delta,
    y: playerShell.position.y + playerShell.velocity.y * delta,
    z: playerShell.position.z + playerShell.velocity.z * delta,
  };
  const shellRangeFraction = projectileRangeFraction(playerShell, shellNext);
  const shellCover = sweepObstacles(playerShell.position, shellNext, playerShell.radius, state.obstacles);
  const threatFraction = threat.kind === 'missile'
    ? movingSphereFraction(
        playerShell.position,
        shellNext,
        threat.position,
        nextThreatPosition,
        playerShell.radius + threat.radius,
      )
    : movingSphereFraction(
        { ...playerShell.position, y: 0 },
        { ...shellNext, y: 0 },
        { ...threat.position, y: 0 },
        { ...nextThreatPosition, y: 0 },
        playerShell.radius + threat.radius,
      );
  const skimmer = state.skimmer;
  const skimmerNext = skimmer
    ? {
        x: skimmer.position.x + skimmer.velocity.x * delta,
        y: skimmer.position.y + skimmer.velocity.y * delta,
        z: skimmer.position.z + skimmer.velocity.z * delta,
      }
    : null;
  const skimmerFraction = skimmer && skimmerNext
    ? movingSphereFraction(
        playerShell.position,
        shellNext,
        skimmer.position,
        skimmerNext,
        playerShell.radius + skimmer.radius,
      )
    : null;
  const hostileShell = state.projectiles.find((projectile) => projectile.owner === 'hostile');
  const hostileNext = hostileShell
    ? {
        x: hostileShell.position.x + hostileShell.velocity.x * delta,
        y: hostileShell.position.y + hostileShell.velocity.y * delta,
        z: hostileShell.position.z + hostileShell.velocity.z * delta,
      }
    : null;
  const hostileCover = hostileShell && hostileNext
    ? sweepObstacles(hostileShell.position, hostileNext, hostileShell.radius, state.obstacles)
    : null;
  const hostileRangeFraction = hostileShell && hostileNext
    ? projectileRangeFraction(hostileShell, hostileNext)
    : null;
  let hostileFraction = hostileShell && hostileNext
    ? movingSphereFraction(
        playerShell.position,
        shellNext,
        hostileShell.position,
        hostileNext,
        playerShell.radius + hostileShell.radius + 0.25,
      )
    : null;
  if (hostileFraction !== null && hostileCover && hostileCover.fraction <= hostileFraction) hostileFraction = null;
  if (hostileFraction !== null && hostileRangeFraction !== null && hostileRangeFraction < hostileFraction) {
    hostileFraction = null;
  }
  const playerFraction = state.spawnProtection <= 0 && hostileShell && hostileNext
    ? movingGroundFraction(
        hostileShell.position,
        hostileNext,
        playerStartPosition,
        playerEndPosition,
        hostileShell.radius + state.player.radius,
      )
    : null;
  const lethalPlayerFraction = playerFraction !== null && (!hostileCover || playerFraction < hostileCover.fraction)
    && (hostileRangeFraction === null || playerFraction <= hostileRangeFraction)
    ? playerFraction
    : null;
  const candidates = [
    shellCover ? { type: 'cover' as const, fraction: shellCover.fraction, point: shellCover.point } : null,
    threatFraction === null
      ? null
      : {
          type: 'threat' as const,
          fraction: threatFraction,
          point: midpoint(
            interpolatePoint(playerShell.position, shellNext, threatFraction),
            interpolatePoint(threat.position, nextThreatPosition, threatFraction),
          ),
        },
    skimmerFraction === null || !skimmer || !skimmerNext
      ? null
      : {
          type: 'skimmer' as const,
          fraction: skimmerFraction,
          point: midpoint(
            interpolatePoint(playerShell.position, shellNext, skimmerFraction),
            interpolatePoint(skimmer.position, skimmerNext, skimmerFraction),
          ),
        },
    hostileFraction === null || !hostileShell || !hostileNext
      ? null
      : {
          type: 'shell' as const,
          fraction: hostileFraction,
          point: midpoint(
            interpolatePoint(playerShell.position, shellNext, hostileFraction),
            interpolatePoint(hostileShell.position, hostileNext, hostileFraction),
          ),
        },
    lethalPlayerFraction === null || !hostileShell || !hostileNext
      ? null
      : {
          type: 'player' as const,
          fraction: lethalPlayerFraction,
          point: interpolatePoint(hostileShell.position, hostileNext, lethalPlayerFraction),
        },
    shellRangeFraction === null
      ? null
      : {
          type: 'range' as const,
          fraction: shellRangeFraction,
          point: interpolatePoint(playerShell.position, shellNext, shellRangeFraction),
        },
  ].filter((candidate) => candidate !== null).sort((left, right) => left.fraction - right.fraction);
  const event = candidates[0];
  if (!event || event.fraction >= bodyFraction) return 'none';

  if (event.type === 'range') {
    state.projectiles = state.projectiles.filter((projectile) => projectile.id !== playerShell.id);
    return 'other';
  }
  spawnImpactParticles(state, event.point, event.type === 'cover' ? 10 : 20);
  if (event.type === 'player') {
    destroyPlayer(state);
    return 'other';
  }
  state.projectiles = state.projectiles.filter((projectile) => projectile.id !== playerShell.id);
  if (event.type === 'threat') {
    destroyPrimaryThreat(state);
    return 'threat';
  }
  if (event.type === 'skimmer') destroySkimmer(state);
  else if (event.type === 'shell' && hostileShell) {
    state.projectiles = state.projectiles.filter((projectile) => projectile.id !== hostileShell.id);
  } else state.events.push({ type: 'shell-impact', position: event.point });
  return 'other';
}

function resolveTankPlayerContact(state: SimulationState, threat: ThreatState, next: Vec3): boolean {
  const minimumDistance = threat.radius + state.player.radius;
  const contactFraction = movingGroundFraction(
    threat.position,
    next,
    state.playerPreviousPosition,
    state.player.position,
    minimumDistance,
  );
  if (contactFraction === null) return false;
  if (state.spawnProtection <= 0) {
    destroyPlayer(state);
    return true;
  }

  const dx = threat.position.x - state.player.position.x;
  const dz = threat.position.z - state.player.position.z;
  const distance = Math.hypot(dx, dz);
  if (distance < minimumDistance) {
    const baseAngle = distance > 0.001 ? Math.atan2(dz, dx) : threat.yaw + Math.PI / 2;
    const angleOffsets = [0, Math.PI / 2, -Math.PI / 2, Math.PI];
    const separated = angleOffsets
      .map((offset) => ({
        x: state.player.position.x + Math.cos(baseAngle + offset) * (minimumDistance + 0.02),
        y: threat.position.y,
        z: state.player.position.z + Math.sin(baseAngle + offset) * (minimumDistance + 0.02),
      }))
      .find((candidate) => !collidesWithAnyObstacle(candidate, threat.radius, state.obstacles));
    if (separated) threat.position = separated;
  } else {
    const travel = Math.hypot(next.x - threat.position.x, next.z - threat.position.z);
    const safeFraction = travel > 0 ? Math.max(0, contactFraction - 0.02 / travel) : 0;
    threat.position = interpolatePoint(threat.position, next, safeFraction);
  }
  state.events.push({ type: 'bump' });
  state.feedback.bump = 0.14;
  return true;
}

function updateMissile(state: SimulationState, threat: ThreatState, delta: number): void {
  const difficulty = difficultyForScore(state.score);
  if (threat.aiMode === 'enter' && threat.aiTime < THREATS.enterWarning) return;
  threat.aiMode = 'seek';
  const dx = state.player.position.x - threat.position.x;
  const dz = state.player.position.z - threat.position.z;
  const targetYaw = Math.atan2(-dx, dz) + Math.sin(state.elapsed * 4.2 + threat.weavePhase) * 0.28;
  threat.yaw = turnToward(threat.yaw, targetYaw, difficulty.missileTurnRate * delta);
  const acceleration = Math.min(1, Math.max(0, (threat.aiTime - THREATS.enterWarning) / 3));
  const speed = THREATS.missileStartSpeed + (THREATS.missileTopSpeed - THREATS.missileStartSpeed) * acceleration;
  const next = {
    x: threat.position.x - Math.sin(threat.yaw) * speed * delta,
    y: Math.max(WORLD.shellHeight, threat.position.y - 1.35 * delta),
    z: threat.position.z + Math.cos(threat.yaw) * speed * delta,
  };
  const cover = sweepObstacles(threat.position, next, threat.radius * 0.45, state.obstacles);
  const playerFraction = movingGroundFraction(
    threat.position,
    next,
    state.playerPreviousPosition,
    state.player.position,
    threat.radius + state.player.radius,
  );
  const physicalFraction = Math.min(
    cover?.fraction ?? Number.POSITIVE_INFINITY,
    playerFraction ?? Number.POSITIVE_INFINITY,
  );
  if (Number.isFinite(physicalFraction)) {
    const earlyEvent = resolvePlayerShellEventBeforeBody(state, threat, next, delta, physicalFraction);
    if (earlyEvent === 'threat' || state.mode !== 'playing') return;
  }
  const collisions = [
    cover ? { type: 'cover' as const, fraction: cover.fraction, point: cover.point } : null,
    playerFraction === null
      ? null
      : {
          type: 'player' as const,
          fraction: playerFraction,
          point: midpoint(
            interpolatePoint(threat.position, next, playerFraction),
            interpolatePoint(state.playerPreviousPosition, state.player.position, playerFraction),
          ),
        },
  ].filter((collision) => collision !== null).sort((left, right) => left.fraction - right.fraction);
  const collision = collisions[0];
  if (collision?.type === 'cover') {
    state.events.push({ type: 'missile-cover-impact', position: collision.point });
    spawnImpactParticles(state, collision.point, 18);
    clearPrimaryThreat(state);
    return;
  }
  if (collision?.type === 'player') {
    spawnImpactParticles(state, collision.point, 20);
    clearPrimaryThreat(state);
    if (state.spawnProtection <= 0) destroyPlayer(state);
    return;
  }
  threat.position = next;
}

function fireHostileShell(state: SimulationState, threat: ThreatState): void {
  if (state.projectiles.some((projectile) => projectile.owner === 'hostile')) return;
  const yaw = threat.yaw;
  const forward = { x: -Math.sin(yaw), z: Math.cos(yaw) };
  const position = {
    x: threat.position.x + forward.x * (threat.radius + 0.8),
    y: WORLD.shellHeight,
    z: threat.position.z + forward.z * (threat.radius + 0.8),
  };
  state.projectiles.push({
    id: state.nextEntityId++,
    owner: 'hostile',
    position,
    previousPosition: { ...position },
    velocity: { x: forward.x * THREATS.enemyShellSpeed, y: 0, z: forward.z * THREATS.enemyShellSpeed },
    distance: 0,
    radius: 0.22,
  });
  state.events.push({ type: 'enemy-fired' });
}

function spawnSkimmer(state: SimulationState): void {
  const side: -1 | 1 = nextRandom(state) < 0.5 ? -1 : 1;
  const forward = { x: -Math.sin(state.player.yaw), z: Math.cos(state.player.yaw) };
  const right = { x: Math.cos(state.player.yaw), z: Math.sin(state.player.yaw) };
  const center = {
    x: state.player.position.x + forward.x * 64,
    z: state.player.position.z + forward.z * 64,
  };
  const position = { x: center.x - right.x * 58 * side, y: 2.8, z: center.z - right.z * 58 * side };
  state.skimmer = {
    id: state.nextEntityId++,
    position,
    previousPosition: { ...position },
    velocity: { x: right.x * 12 * side, y: 0, z: right.z * 12 * side },
    radius: 1.45,
    age: 0,
  };
}

function updateSkimmer(state: SimulationState, delta: number): void {
  const skimmer = state.skimmer;
  if (!skimmer) return;
  skimmer.previousPosition = { ...skimmer.position };
  skimmer.position.x += skimmer.velocity.x * delta;
  skimmer.position.z += skimmer.velocity.z * delta;
  skimmer.age += delta;
  if (skimmer.age >= THREATS.skimmerVisit) {
    state.skimmer = null;
    state.skimmerCooldown = randomRange(state, THREATS.skimmerCooldown[0], THREATS.skimmerCooldown[1]);
  }
}

function firePlayerShell(state: SimulationState): void {
  const forward = { x: -Math.sin(state.player.yaw), z: Math.cos(state.player.yaw) };
  const position = {
    x: state.player.position.x + forward.x * PLAYER.muzzleClearance,
    y: WORLD.shellHeight,
    z: state.player.position.z + forward.z * PLAYER.muzzleClearance,
  };
  state.projectiles.push({
    id: state.nextEntityId++,
    owner: 'player',
    position,
    previousPosition: { ...position },
    velocity: { x: forward.x * PLAYER.shellSpeed, y: 0, z: forward.z * PLAYER.shellSpeed },
    distance: 0,
    radius: 0.18,
  });
  state.events.push({ type: 'player-fired' });
  state.feedback.muzzleFlash = 0.12;
  state.feedback.recoil = 0.16;
}

function updateProjectiles(state: SimulationState, delta: number): void {
  const survivors: ProjectileState[] = [];
  const removedIds = new Set<number>();
  const ordered = [...state.projectiles].sort((a, b) => (a.owner === b.owner ? a.id - b.id : a.owner === 'player' ? -1 : 1));
  for (const projectile of ordered) {
    if (removedIds.has(projectile.id)) continue;
    projectile.previousPosition = { ...projectile.position };
    const movement = {
      x: projectile.velocity.x * delta,
      y: projectile.velocity.y * delta,
      z: projectile.velocity.z * delta,
    };
    const movementDistance = Math.hypot(movement.x, movement.y, movement.z);
    const next = {
      x: projectile.position.x + movement.x,
      y: projectile.position.y + movement.y,
      z: projectile.position.z + movement.z,
    };
    const rangeFraction = projectileRangeFraction(projectile, next);
    projectile.distance += movementDistance;
    const cover = sweepObstacles(projectile.position, next, projectile.radius, state.obstacles);
    if (projectile.owner === 'player') {
      const target = state.primaryThreat;
      const targetFraction = target
        ? target.kind === 'missile'
          ? movingSphereFraction(
              projectile.position,
              next,
              target.previousPosition,
              target.position,
              projectile.radius + target.radius,
            )
          : movingSphereFraction(
              { ...projectile.position, y: 0 },
              { ...next, y: 0 },
              { ...target.previousPosition, y: 0 },
              { ...target.position, y: 0 },
              projectile.radius + target.radius,
            )
        : null;
      const skimmer = state.skimmer;
      const skimmerFraction = skimmer
        ? movingSphereFraction(
            projectile.position,
            next,
            skimmer.previousPosition,
            skimmer.position,
            projectile.radius + skimmer.radius,
          )
        : null;
      const hostileShell = state.projectiles.find(
        (candidate) => candidate.owner === 'hostile' && !removedIds.has(candidate.id),
      );
      const hostileNext = hostileShell
        ? {
            x: hostileShell.position.x + hostileShell.velocity.x * delta,
            y: hostileShell.position.y + hostileShell.velocity.y * delta,
            z: hostileShell.position.z + hostileShell.velocity.z * delta,
          }
        : null;
      const hostileCover = hostileShell && hostileNext
        ? sweepObstacles(hostileShell.position, hostileNext, hostileShell.radius, state.obstacles)
        : null;
      const hostileRangeFraction = hostileShell && hostileNext
        ? projectileRangeFraction(hostileShell, hostileNext)
        : null;
      const hostilePlayerFraction = hostileShell && hostileNext
        ? movingGroundFraction(
            hostileShell.position,
            hostileNext,
            state.playerPreviousPosition,
            state.player.position,
            hostileShell.radius + state.player.radius,
          )
        : null;
      let hostileFraction = hostileShell && hostileNext
        ? movingSphereFraction(
            projectile.position,
            next,
            hostileShell.position,
            hostileNext,
            projectile.radius + hostileShell.radius + 0.25,
          )
        : null;
      if (hostileFraction !== null && hostileCover && hostileCover.fraction <= hostileFraction) hostileFraction = null;
      if (hostileFraction !== null && hostileRangeFraction !== null && hostileRangeFraction < hostileFraction) {
        hostileFraction = null;
      }
      if (
        hostileFraction !== null &&
        hostilePlayerFraction !== null &&
        hostilePlayerFraction <= hostileFraction &&
        (!hostileCover || hostilePlayerFraction < hostileCover.fraction)
      ) hostileFraction = null;
      const lethalHostileFraction = state.spawnProtection <= 0 &&
        hostilePlayerFraction !== null &&
        (!hostileCover || hostilePlayerFraction < hostileCover.fraction) &&
        (hostileRangeFraction === null || hostilePlayerFraction <= hostileRangeFraction)
        ? hostilePlayerFraction
        : null;
      const collisions = [
        cover ? { type: 'cover' as const, fraction: cover.fraction, point: cover.point } : null,
        targetFraction === null
          ? null
          : {
              type: 'threat' as const,
              fraction: targetFraction,
              point: target
                ? midpoint(
                    interpolatePoint(projectile.position, next, targetFraction),
                    interpolatePoint(target.previousPosition, target.position, targetFraction),
                  )
                : interpolatePoint(projectile.position, next, targetFraction),
            },
        skimmerFraction === null
          ? null
          : {
              type: 'skimmer' as const,
              fraction: skimmerFraction,
              point: skimmer
                ? midpoint(
                    interpolatePoint(projectile.position, next, skimmerFraction),
                    interpolatePoint(skimmer.previousPosition, skimmer.position, skimmerFraction),
                  )
                : interpolatePoint(projectile.position, next, skimmerFraction),
            },
        hostileFraction === null
          ? null
          : {
              type: 'shell' as const,
              fraction: hostileFraction,
              point: hostileShell && hostileNext
                ? midpoint(
                    interpolatePoint(projectile.position, next, hostileFraction),
                    interpolatePoint(hostileShell.position, hostileNext, hostileFraction),
                  )
                : interpolatePoint(projectile.position, next, hostileFraction),
            },
        rangeFraction === null
          ? null
          : {
              type: 'range' as const,
              fraction: rangeFraction,
              point: interpolatePoint(projectile.position, next, rangeFraction),
            },
      ].filter((collision) => collision !== null).sort((a, b) => a.fraction - b.fraction);
      const collision = collisions[0];
      if (collision && (lethalHostileFraction === null || collision.fraction < lethalHostileFraction)) {
        if (collision.type === 'range') continue;
        spawnImpactParticles(state, collision.point, collision.type === 'cover' ? 10 : 20);
        if (collision.type === 'threat') destroyPrimaryThreat(state);
        else if (collision.type === 'skimmer') destroySkimmer(state);
        else if (collision.type === 'shell' && hostileShell) removedIds.add(hostileShell.id);
        else state.events.push({ type: 'shell-impact', position: collision.point });
        continue;
      }
    } else {
      const playerFraction = movingGroundFraction(
        projectile.position,
        next,
        state.playerPreviousPosition,
        state.player.position,
        projectile.radius + state.player.radius,
      );
      const collisions = [
        cover ? { type: 'cover' as const, fraction: cover.fraction, point: cover.point } : null,
        playerFraction === null
          ? null
          : {
              type: 'player' as const,
              fraction: playerFraction,
              point: midpoint(
                interpolatePoint(projectile.position, next, playerFraction),
                interpolatePoint(state.playerPreviousPosition, state.player.position, playerFraction),
              ),
            },
        rangeFraction === null
          ? null
          : {
              type: 'range' as const,
              fraction: rangeFraction,
              point: interpolatePoint(projectile.position, next, rangeFraction),
            },
      ].filter((collision) => collision !== null).sort((left, right) => left.fraction - right.fraction);
      const collision = collisions[0];
      if (collision?.type === 'range') continue;
      if (collision?.type === 'cover') {
        spawnImpactParticles(state, collision.point, 10);
        state.events.push({ type: 'shell-impact', position: collision.point });
        continue;
      }
      if (collision?.type === 'player') {
        spawnImpactParticles(state, collision.point, 18);
        if (state.spawnProtection <= 0) {
          destroyPlayer(state);
          if (state.mode === 'life-lost') {
            state.projectiles = [];
            return;
          }
        }
        continue;
      }
    }
    projectile.position = next;
    if (rangeFraction === null) survivors.push(projectile);
  }
  state.projectiles = survivors.filter((projectile) => !removedIds.has(projectile.id));
}

export function destroyPlayer(state: SimulationState): void {
  if (state.mode !== 'playing' || state.spawnProtection > 0) return;
  state.tanks = Math.max(0, state.tanks - 1);
  state.projectiles = [];
  state.primaryThreat = null;
  state.skimmer = null;
  state.radar.blips = [];
  state.player.drive = { leftTread: 0, rightTread: 0 };
  state.events.push({ type: 'player-destroyed' });
  state.feedback.damageFlash = 1;
  setFeedbackMessage(state, 'ARMOR BREACH', 1.4);
  changeMode(state, 'life-lost');
}

function destroyPrimaryThreat(state: SimulationState): void {
  const threat = state.primaryThreat;
  if (!threat) return;
  const points = SCORING[threat.kind];
  state.events.push({ type: 'target-destroyed', target: threat.kind, points });
  setFeedbackMessage(state, `TARGET +${points}`, 1.1);
  addScore(state, points);
  clearPrimaryThreat(state);
}

function clearPrimaryThreat(state: SimulationState): void {
  state.primaryThreat = null;
  state.threatIntermission = randomRange(state, FLOW.threatIntermissionMin, FLOW.threatIntermissionMax);
}

function destroySkimmer(state: SimulationState): void {
  if (!state.skimmer) return;
  state.events.push({ type: 'target-destroyed', target: 'skimmer', points: SCORING.skimmer });
  setFeedbackMessage(state, `SKIMMER +${SCORING.skimmer}`, 1.1);
  addScore(state, SCORING.skimmer);
  state.skimmer = null;
  state.skimmerCooldown = randomRange(state, THREATS.skimmerCooldown[0], THREATS.skimmerCooldown[1]);
}

export function addScore(state: SimulationState, points: number): void {
  state.score = Math.max(0, state.score + Math.max(0, Math.floor(points)));
  while (
    state.awardedBonuses < SCORING.bonusThresholds.length &&
    state.score >= (SCORING.bonusThresholds[state.awardedBonuses] ?? Number.POSITIVE_INFINITY)
  ) {
    const threshold = SCORING.bonusThresholds[state.awardedBonuses];
    if (threshold === undefined) break;
    state.awardedBonuses += 1;
    state.tanks += 1;
    state.events.push({ type: 'bonus-tank', threshold });
    state.feedback.bonusFlash = 1;
    setFeedbackMessage(state, 'BONUS TANK', 1.8);
  }
}

function hasLineOfSight(start: Vec3, endGround: Vec3, state: SimulationState): boolean {
  const end = { x: endGround.x, y: WORLD.shellHeight, z: endGround.z };
  const elevatedStart = { x: start.x, y: WORLD.shellHeight, z: start.z };
  return sweepObstacles(elevatedStart, end, 0.12, state.obstacles) === null;
}

function feelerBlocked(
  state: SimulationState,
  startGround: Vec3,
  yaw: number,
  length: number,
  radius: number,
): boolean {
  const start = { x: startGround.x, y: WORLD.shellHeight, z: startGround.z };
  const end = {
    x: start.x - Math.sin(yaw) * length,
    y: WORLD.shellHeight,
    z: start.z + Math.cos(yaw) * length,
  };
  return sweepObstacles(start, end, radius, state.obstacles) !== null;
}

function findSafeRespawn(state: SimulationState, origin: Vec3): Vec3 {
  const candidates: Vec3[] = [{ ...origin }];
  for (let ring = 1; ring <= 8; ring += 1) {
    for (let step = 0; step < 12; step += 1) {
      const angle = (step / 12) * TAU;
      candidates.push({
        x: origin.x + Math.cos(angle) * ring * 8,
        y: 0,
        z: origin.z + Math.sin(angle) * ring * 8,
      });
    }
  }
  return (
    candidates.find((candidate) => {
      const obstacles = buildWorldAround(state.seed, candidate.x, candidate.z, WORLD.activeCellRadius);
      return !collidesWithAnyObstacle(candidate, PLAYER.radius + 12.65, obstacles);
    }) ?? { ...origin }
  );
}

function refreshActiveWorld(state: SimulationState): void {
  const centers = state.primaryThreat
    ? [state.player.position, state.primaryThreat.position]
    : [state.player.position];
  state.obstacles = buildWorldAroundPoints(state.seed, centers, WORLD.activeCellRadius);
}

function spawnImpactParticles(state: SimulationState, position: Vec3, count = 10): void {
  for (let index = 0; index < count && state.particles.length < WORLD.particleLimit; index += 1) {
    const angle = randomRange(state, 0, TAU);
    const speed = randomRange(state, 1.8, 7.5);
    state.particles.push({
      id: state.nextEntityId++,
      position: { ...position },
      velocity: {
        x: Math.cos(angle) * speed,
        y: randomRange(state, 1.2, 6.5),
        z: Math.sin(angle) * speed,
      },
      life: randomRange(state, 0.28, 0.78),
      maxLife: 0.78,
    });
  }
}

function updateParticles(state: SimulationState, delta: number): void {
  const survivors: ParticleState[] = [];
  for (const particle of state.particles) {
    particle.life -= delta;
    if (particle.life <= 0) continue;
    particle.position.x += particle.velocity.x * delta;
    particle.position.y += particle.velocity.y * delta;
    particle.position.z += particle.velocity.z * delta;
    particle.velocity.y -= 8 * delta;
    survivors.push(particle);
  }
  state.particles = survivors;
}

function updateFeedback(state: SimulationState, delta: number): void {
  state.feedback.muzzleFlash = Math.max(0, state.feedback.muzzleFlash - delta);
  state.feedback.recoil = Math.max(0, state.feedback.recoil - delta);
  state.feedback.bump = Math.max(0, state.feedback.bump - delta);
  state.feedback.damageFlash = Math.max(0, state.feedback.damageFlash - delta * 1.25);
  state.feedback.bonusFlash = Math.max(0, state.feedback.bonusFlash - delta * 1.1);
  state.feedback.messageTime = Math.max(0, state.feedback.messageTime - delta);
  if (state.feedback.messageTime === 0) state.feedback.message = '';
}

function setFeedbackMessage(state: SimulationState, message: string, duration: number): void {
  state.feedback.message = message;
  state.feedback.messageTime = duration;
}

function changeMode(state: SimulationState, mode: SimulationState['mode']): void {
  state.mode = mode;
  state.modeTime = 0;
  state.events.push({ type: 'mode-changed', mode });
}

function nextRandom(state: SimulationState): number {
  state.randomState = (state.randomState + 0x6d2b79f5) >>> 0;
  let value = state.randomState;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
}

function randomRange(state: SimulationState, minimum: number, maximum: number): number {
  return minimum + (maximum - minimum) * nextRandom(state);
}

function normalizeAngle(angle: number): number {
  return ((angle + Math.PI) % TAU + TAU) % TAU - Math.PI;
}

function turnToward(current: number, target: number, maximumDelta: number): number {
  const difference = normalizeAngle(target - current);
  if (Math.abs(difference) <= maximumDelta) return normalizeAngle(target);
  return normalizeAngle(current + Math.sign(difference) * maximumDelta);
}

function interpolatePoint(start: Vec3, end: Vec3, fraction: number): Vec3 {
  return {
    x: start.x + (end.x - start.x) * fraction,
    y: start.y + (end.y - start.y) * fraction,
    z: start.z + (end.z - start.z) * fraction,
  };
}

function midpoint(first: Vec3, second: Vec3): Vec3 {
  return {
    x: (first.x + second.x) / 2,
    y: (first.y + second.y) / 2,
    z: (first.z + second.z) / 2,
  };
}

function movingGroundFraction(
  firstStart: Vec3,
  firstEnd: Vec3,
  secondStart: Vec3,
  secondEnd: Vec3,
  radius: number,
): number | null {
  return movingSphereFraction(
    { ...firstStart, y: 0 },
    { ...firstEnd, y: 0 },
    { ...secondStart, y: 0 },
    { ...secondEnd, y: 0 },
    radius,
  );
}

function projectileRangeFraction(projectile: ProjectileState, end: Vec3): number | null {
  const maximumRange = projectile.owner === 'player' ? PLAYER.shellRange : THREATS.enemyShellRange;
  const remainingRange = maximumRange - projectile.distance;
  if (remainingRange <= 0) return 0;
  const stepDistance = Math.hypot(
    end.x - projectile.position.x,
    end.y - projectile.position.y,
    end.z - projectile.position.z,
  );
  if (stepDistance === 0 || remainingRange > stepDistance) return null;
  return remainingRange / stepDistance;
}
